import { Check, Copy, Download, KeyRound, QrCode, ShieldAlert, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, Modal } from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type Peer } from '@/lib/api';
import { cn } from '@/lib/cn';
import { copyToClipboard, downloadText } from '@/lib/format';

interface Props {
  open: boolean;
  onClose: () => void;
  instanceId: string;
  peer: Peer | null;
  /** Die Konfiguration wird genau einmal ausgeliefert und hier hereingereicht. */
  config: string | null;
  /** Erzeugt ein neues Schlüsselpaar, wenn keine Konfiguration mehr vorliegt. */
  onRotate?: () => void | Promise<void>;
}

/**
 * Zeigt die Client-Konfiguration als QR-Code und Text.
 *
 * Zero-Knowledge: der private Schlüssel wird serverseitig nie gespeichert. Er
 * existiert nur in dieser Antwort - deshalb ist der Dialog der einzige Ort, an
 * dem die vollständige Konfiguration erscheint. Wer sie verliert, erzeugt über
 * "Neuen Schlüssel erzeugen" ein frisches Schlüsselpaar.
 */
export function PeerConfigDialog({ open, onClose, instanceId, peer, config, onRotate }: Props) {
  const t = useT();
  const notify = useNotify();
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<'qr' | 'text'>('qr');
  /**
   * Nachgeladene Fassung ohne privaten Schluessel. Sie kommt zum Zuge, wenn der
   * Dialog fuer einen bestehenden Peer geoeffnet wird - die vollstaendige
   * Konfiguration gibt es nur einmal, direkt nach dem Anlegen.
   */
  const [stored, setStored] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !peer) {
      setStored(null);
      return;
    }
    if (config) return;

    let cancelled = false;
    api.instances.peers
      .config(instanceId, peer.id)
      .then((result) => {
        if (!cancelled) setStored(result.config);
      })
      .catch(() => {
        if (!cancelled) setStored(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, peer, config, instanceId]);

  // Die ausgelieferte Konfiguration hat Vorrang - nur sie traegt den Schluessel.
  const shown = config ?? stored;
  const withoutKey = !config && Boolean(stored);

  useEffect(() => {
    if (!open || !peer || !shown) {
      setQrSvg(null);
      return;
    }
    setView('qr');
    let cancelled = false;

    api.instances.peers
      .qrCode(instanceId, peer.id, shown)
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, peer, shown, instanceId]);

  if (!peer) return null;

  const handleCopy = async () => {
    if (!shown) return;
    if (!(await copyToClipboard(shown))) {
      notify.error(t('config.copyFailed'));
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const tab = (key: 'qr' | 'text', label: string, icon: React.ReactNode) => (
    <button
      type="button"
      onClick={() => setView(key)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-vela-sm)] px-3 py-1.5 text-xs font-bold transition-colors',
        view === key ? 'bg-bg-3 text-t0' : 'text-t1 hover:text-t0',
      )}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t('config.title', { name: peer.name })}
      footer={
        shown ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={handleCopy}
              icon={copied ? <Check size={15} className="text-ok" /> : <Copy size={15} />}
            >
              {copied ? t('common.copied') : t('config.copyText')}
            </Button>
            <Button
              icon={<Download size={15} />}
              onClick={() => downloadText(`${sanitize(peer.name)}.conf`, shown)}
            >
              {t('config.downloadConf')}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('common.close')}
            </Button>
            {onRotate && (
              <Button icon={<KeyRound size={15} />} onClick={() => void onRotate()}>
                {t('peers.rotate')}
              </Button>
            )}
          </div>
        )
      }
    >
      <p className="-mt-1 mb-4 font-mono text-xs text-t2">
        {peer.ipv4Address}
        {peer.ipv6Address ? ` / ${peer.ipv6Address}` : ''}
      </p>

      {shown ? (
        <>
          {/*
            Zwei verschiedene Lagen: direkt nach dem Anlegen traegt die Datei den
            privaten Schluessel und ist genau einmal zu haben. Beim spaeteren
            Nachschlagen fehlt er - dann ist der Hinweis ein anderer.
          */}
          {withoutKey ? (
            <div className="mb-4 flex items-start gap-2 rounded-[var(--radius-vela-md)] border border-line bg-info-soft px-3 py-2.5 text-[13px] text-info">
              <KeyRound size={16} className="mt-0.5 shrink-0" />
              <span>
                <strong className="font-bold">{t('peers.configWithoutKey')}</strong>
                <br />
                {t('peers.configWithoutKeyText')}
              </span>
            </div>
          ) : (
            <div className="mb-4 flex items-start gap-2 rounded-[var(--radius-vela-md)] border border-line bg-warn-soft px-3 py-2.5 text-[13px] text-warn">
              <ShieldAlert size={16} className="mt-0.5 shrink-0" />
              <span>
                <strong className="font-bold">{t('config.onceOnlyTitle')}</strong>
                <br />
                {t('config.onceOnlyText')}
              </span>
            </div>
          )}

          <div className="mb-4 inline-flex rounded-[var(--radius-vela-md)] border border-line bg-bg-inset p-0.5">
            {tab('qr', t('config.qrTab'), <QrCode size={14} />)}
            {tab('text', t('config.fileTab'), null)}
          </div>

          {view === 'qr' ? (
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-[var(--radius-vela-lg)] bg-white p-4">
                {qrSvg ? (
                  <div
                    className="h-[280px] w-[280px] [&>svg]:h-full [&>svg]:w-full"
                    role="img"
                    aria-label={t('config.title', { name: peer.name })}
                    dangerouslySetInnerHTML={{ __html: qrSvg }}
                  />
                ) : (
                  <div className="flex h-[280px] w-[280px] items-center justify-center">
                    <span
                      className="h-7 w-7 rounded-full border-2 border-zinc-300 border-t-zinc-600"
                      style={{ animation: 'velaSpin .7s linear infinite' }}
                    />
                  </div>
                )}
              </div>
              <p className="flex items-center gap-2 text-center text-xs text-t2">
                <Smartphone size={14} />
                {t('config.scanHint')}
              </p>
            </div>
          ) : (
            <pre className="max-h-80 overflow-auto rounded-[var(--radius-vela-md)] border border-line bg-bg-inset p-4 font-mono text-xs leading-relaxed text-t1">
              {shown}
            </pre>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-3 text-t2">
            <KeyRound size={22} />
          </span>
          <div>
            <p className="text-[14px] font-bold text-t0">{t('config.noStoredKey')}</p>
            <p className="mx-auto mt-1 max-w-sm text-[13px] text-t1">{t('config.noStoredKeyText')}</p>
          </div>
        </div>
      )}
    </Modal>
  );
}

function sanitize(name: string): string {
  return name.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'wireguard';
}
